import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { message } from "antd";
import debounce from "lodash/debounce";
import { getB2BCustomers, validateGSTNumber, validatePANNo, getLoyaltyInfo } from "../../../../services/index";
import { getCustomer } from "../customer";
import useReferenceStore from "../../../../stores/useReferenceStore";
import env from "../../../../config/env";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";

/**
 * Custom hook encapsulating all data-fetching, mapping, and field-change logic
 * for the Add/Edit Customer drawer.
 */
const useCustomerFormData = ({
  customerForm,
  form,
  activeTab,
  activeTabName,
  isB2BEnabled,
  setIsB2BEnabled,
  formDataCache,
  setFormDataCache,
  retailCustomerFields,
  retailCustomerApiDataSources,
  groupedFields,
  cart,
  tillData,
  posConfig,
  customerConfigData,
  loading,
  setLoading,
  setSelectedEditOldCustomer,
  setIsPanUpdateMode,
  setPanCustomerInfo,
  setKeyboardInputFields,
  keyboardInputFields,
  setKioskUI,
  kioskLogin,
  pendingRefundCustomer,
  setPendingRefundCustomer,
  isRefundCustomerEdit,
  setIsRefundCustomerEdit,
  setCustomerSearchResults,
  displayAddNewCustomer,
  displayEditOldCustomer,
  setDocuments,
  setIsPanCustomer,
}) => {
  const [optionsMap, setOptionsMap] = useState({});
  const [b2bSearchResults, setB2bSearchResults] = useState([]);
  const [b2bLoading, setB2bLoading] = useState(false);
  const [searchPending, setSearchPending] = useState(false);
  const [searchByName, setSearchByName] = useState(false);
  const [noB2BResults, setNoB2BResults] = useState(false);

  // Tracks exactly which form keys the last PAN auto-fill touched, plus
  // their pre-validation values, so disabling the PAN toggle can roll
  // those specific fields back instead of guessing by key name.
  const panAutoFillRef = useRef({ keys: [], snapshot: {} });

  const latestSearch = useRef("");
  const abortController = useRef(null);

  // ─── Utilities ─────────────────────────────────────────────────────────────

  const toCamelCase = (str) => {
    if (!str) return "";
    return str
      .trim()
      .replace(/[^a-zA-Z0-9 ]/g, " ")
      .split(" ")
      .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join("");
  };

  const normalizeKey = (str = "") => str.toLowerCase().replace(/[^a-z0-9]/g, "");

  const normalizeStaticSelectValue = (value, staticJson) => {
    if (!value || !staticJson) return value;
    try {
      const parsed = JSON.parse(staticJson);
      if (parsed[value]) return value;
      const reverseMap = Object.fromEntries(Object.entries(parsed).map(([k, v]) => [v, k]));
      return reverseMap[value] || value;
    } catch {
      return value;
    }
  };

  const getStaticDisplayValue = (field, savedKey) => {
    if (!field || !field.defaultValue || !savedKey) return "";
    try {
      const parsed = JSON.parse(field.defaultValue);
      return parsed[savedKey] || savedKey;
    } catch {
      return savedKey;
    }
  };

  const flattenObject = (obj, prefix = "", res = {}) => {
    for (const key in obj) {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        flattenObject(obj[key], prefix + key + ".", res);
      } else {
        res[prefix + key] = obj[key];
      }
    }
    return res;
  };

  // ─── handleChange ──────────────────────────────────────────────────────────

  const handleChange = (e, field, groupId) => {
    const value = e?.target?.value ?? e;
    const fieldKey = toCamelCase(field.name);
    const groupKey = groupId ? String(groupId).toLowerCase() : null;

    setFormDataCache((prev) => {
      const tabData = prev[activeTab] || {};
      let newTabData;
      if (!groupKey) {
        newTabData = { ...tabData, [fieldKey]: value };
      } else {
        newTabData = { ...tabData, [groupKey]: { ...(tabData[groupKey] || {}), [fieldKey]: value } };
      }
      const groupData = groupKey ? newTabData[groupKey] || {} : newTabData;
      const firstName = groupData[toCamelCase("First Name")] || "";
      const middleName = groupData[toCamelCase("Middle Name")] || "";
      const lastName = groupData[toCamelCase("Last Name")] || "";

      if (field.name === "First Name" || field.name === "Middle Name" || field.name === "Last Name") {
        const fullName = [firstName, middleName, lastName].filter(Boolean).join(" ");
        if (groupKey) {
          customerForm.setFieldsValue({ [groupKey]: { ...customerForm.getFieldValue(groupKey), [toCamelCase("Name")]: fullName } });
          newTabData = { ...newTabData, [groupKey]: { ...(newTabData[groupKey] || {}), [toCamelCase("Name")]: fullName } };
        } else {
          customerForm.setFieldsValue({ [toCamelCase("Name")]: fullName });
          newTabData = { ...newTabData, [toCamelCase("Name")]: fullName };
        }
        setKeyboardInputFields((prevKeys) => ({ ...prevKeys, [toCamelCase("Name")]: fullName }));
      }
      return { ...prev, [activeTab]: newTabData };
    });
  };

  // ─── B2B search ────────────────────────────────────────────────────────────

  const _debouncedB2BSearch = useMemo(
    () =>
      debounce(async (value) => {
        setSearchPending(false);
        latestSearch.current = value;
        if (!value.trim()) {
          if (abortController.current) abortController.current.abort();
          setB2bSearchResults([]);
          setB2bLoading(false);
          return;
        }
        if (abortController.current) abortController.current.abort();
        const controller = new AbortController();
        abortController.current = controller;
        setB2bLoading(true);
        setB2bSearchResults([]);
        try {
          const queryValue = searchByName ? value.trim() : value.trim().toUpperCase();
          const resp = await getB2BCustomers({ q: queryValue, startRow: "0", endRow: "20" }, { signal: controller.signal });
          const customers = resp?.data?.getB2BCustomerList?.customers || [];
          if (latestSearch.current !== value) return;
          setB2bSearchResults(customers);
          setNoB2BResults(customers.length === 0);
          if (customers.length === 0) {
            const trimmedValue = value.trim();
            if (!trimmedValue) return;
            const autoFillValues = {};
            groupedFields.forEach((group) => {
              group.fields.forEach((field) => {
                const fieldName = field.name.toLowerCase();
                if (searchByName && fieldName === "company name") {
                  if (group.groupId) {
                    autoFillValues[group.groupId] = { ...(autoFillValues[group.groupId] || {}), [field.name]: trimmedValue };
                  } else {
                    autoFillValues[field.name] = trimmedValue;
                  }
                }
                if (!searchByName && fieldName.includes("gst")) {
                  if (group.groupId) {
                    autoFillValues[group.groupId] = { ...(autoFillValues[group.groupId] || {}), [field.name]: trimmedValue.toUpperCase() };
                  } else {
                    autoFillValues[field.name] = trimmedValue.toUpperCase();
                  }
                }
              });
            });
            customerForm.setFieldsValue(autoFillValues);
            setFormDataCache((prev) => ({ ...prev, [activeTab]: { ...(prev[activeTab] || {}), ...autoFillValues } }));
          }
        } catch (err) {
          if (err.name !== "AbortError") console.error("SEARCH ERROR ->", err);
        } finally {
          setB2bLoading(false);
        }
      }, 300),
    [searchByName]
  );

  const searchB2BCustomers = useCallback(
    (value) => {
      if (!value?.trim()) {
        setSearchPending(false);
        _debouncedB2BSearch.cancel();
        if (abortController.current) abortController.current.abort();
        setB2bSearchResults([]);
        setB2bLoading(false);
        return;
      }
      setSearchPending(true);
      _debouncedB2BSearch(value);
    },
    [_debouncedB2BSearch]
  );

  // ─── B2B data mapping ──────────────────────────────────────────────────────

  const mapB2BDataDynamic = (customer, gFields, isAddMode = false) => {
    const formValues = {};
    const safeJoin = (...parts) => parts.filter(Boolean).join(", ").trim();
    const toCamel = (str) =>
      str
        .trim()
        .replace(/[^a-zA-Z0-9 ]/g, " ")
        .split(" ")
        .map((word, index) => (index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
        .join("");

    gFields.forEach((group) => {
      const groupId = group.groupId ? String(group.groupId) : null;
      const groupNameLower = group.groupName?.toLowerCase() || "";
      const isInvoiceGroup = groupNameLower.includes("invoice") || groupNameLower.includes("invoicing");
      const isDeliveryGroup = groupNameLower.includes("delivery");
      const address = isInvoiceGroup ? customer.invoiceAddresses?.[0] : isDeliveryGroup ? customer.deliveryAddresses?.[0] : null;
      const matchingLoc = address ? customer.localisation?.find((l) => l.addressId === address.s_customer_address_id) : customer.localisation?.[0] || null;

      group.fields.forEach((field) => {
        if (!field.visible || !field.tabId) return;
        const rawLabel = field.name || "";
        const labelTrimmed = rawLabel.trim();
        const key = labelTrimmed.toLowerCase();
        const fieldKey = toCamel(labelTrimmed);
        let value = null;

        if (address && (isInvoiceGroup || isDeliveryGroup)) {
          if (key.includes("street")) value = safeJoin(address.line1, address.line2);
          else if (key.includes("line1") || key.includes("line 1")) value = address.line1 || "";
          else if (key.includes("line2") || key.includes("line 2")) value = address.line2 || "";
          else if (key.includes("city")) value = address.city || "";
          else if (key.includes("state") || key.includes("region")) value = address.regionName || "";
          else if (key.includes("country")) value = address.countryName || "";
          else if (key.includes("pincode") || key.includes("zip") || key.includes("postal")) value = address.pincode || "";
          else if (key.includes("gst") && matchingLoc) value = matchingLoc.gstType || "";
          else if (key.includes("company type") || (key.includes("company") && key.includes("type"))) value = matchingLoc?.companyType || "";
        } else {
          if (key.includes("customer code")) value = customer.customerCode || "";
          else if (key.includes("customer name")) value = customer.customerName || "";
          else if (key.includes("tax") && key.includes("id")) value = customer.taxId || "";
          else if (key === "company name" || key.includes("company name")) value = customer.companyName || "";
          else if (key === "gst no" || key.includes("gst no")) value = customer.gstNo || customer.localisation?.[0]?.gstNo || "";
          else if (key.includes("company name") || (key.includes("company") && !key.includes("type")))
            value = matchingLoc?.companyType || customer.localisation?.[0]?.companyType || "";
        }

        if (value !== null && value !== undefined && value !== "") {
          if (groupId) {
            formValues[groupId] = formValues[groupId] || {};
            formValues[groupId][rawLabel] = value;
            if (rawLabel !== labelTrimmed) formValues[groupId][labelTrimmed] = value;
            formValues[groupId][fieldKey] = value;
          } else {
            formValues[rawLabel] = value;
            if (rawLabel !== labelTrimmed) formValues[labelTrimmed] = value;
            formValues[fieldKey] = value;
          }
        }
      });
    });

    if (isAddMode) {
      const flattened = {};
      Object.entries(formValues).forEach(([groupId, fields]) => {
        if (typeof fields === "object" && fields !== null) {
          Object.entries(fields).forEach(([label, val]) => {
            flattened[[groupId, label]] = val;
          });
        } else {
          flattened[groupId] = fields;
        }
      });
      return flattened;
    }
    return formValues;
  };

  const fillAddressFields = (address, groupOrId, type, customer) => {
    if (!address || !groupOrId) return;
    const groupId = typeof groupOrId === "object" ? groupOrId.groupId : groupOrId;
    if (!groupId && groupId !== 0) return;

    const safe = (v) => (v === undefined || v === null ? "" : v);
    const toCamel = (str) =>
      String(str || "")
        .replace(/[^a-zA-Z0-9 ]/g, " ")
        .trim()
        .split(/\s+/)
        .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
        .join("");
    const trimLabel = (s) => String(s || "").trim();
    const loc = customer?.localisation?.find((l) => l.addressId === address.s_customer_address_id) || {};

    const rawToValue = {
      "Street Address": safe(address.line1) + (address.line2 ? ", " + safe(address.line2) : ""),
      City: safe(address.city),
      State: safe(address.regionName),
      Country: safe(address.countryName),
      Pincode: safe(address.pincode),
      "GST Type": safe(loc.gstType),
      "Company Type": safe(loc.companyType),
      "GST Number": safe(loc.gstNo),
      "Pan Number": safe(loc.panNo),
    };

    const groupVariants = new Set();
    groupVariants.add(groupId);
    groupVariants.add(String(groupId));
    groupVariants.add(String(groupId).toLowerCase());
    if (!Number.isNaN(Number(groupId))) groupVariants.add(Number(groupId));

    const fieldsPayload = {};
    const canonicalGroupKey = String(groupId);
    const canonicalForCache = {};

    Object.entries(rawToValue).forEach(([rawLabel, val]) => {
      const trimmed = trimLabel(rawLabel);
      const camel = toCamel(trimmed);
      canonicalForCache[rawLabel] = val;
      if (trimmed !== rawLabel) canonicalForCache[trimmed] = val;
      canonicalForCache[camel] = val;
      groupVariants.forEach((gKey) => {
        if (!fieldsPayload[gKey]) fieldsPayload[gKey] = {};
        fieldsPayload[gKey][rawLabel] = val;
        fieldsPayload[gKey][trimmed] = val;
        fieldsPayload[gKey][camel] = val;
      });
    });

    try {
      customerForm.setFieldsValue(fieldsPayload);
    } catch (err) {
      Object.entries(fieldsPayload).forEach(([gKey, obj]) => {
        customerForm.setFieldsValue({ [gKey]: { ...(customerForm.getFieldValue(gKey) || {}), ...obj } });
      });
    }

    setFormDataCache((prev) => ({
      ...prev,
      [activeTab]: {
        ...(prev[activeTab] || {}),
        [canonicalGroupKey]: { ...(prev[activeTab]?.[canonicalGroupKey] || {}), ...canonicalForCache },
      },
    }));
  };

  // ─── GST helpers ───────────────────────────────────────────────────────────

  const normalizeGSTResponse = (gst) => {
    if (!gst || !gst.valid) return null;
    const flat = {};
    const nKey = (key) =>
      key.includes("_") || key.includes(" ")
        ? key
            .replace(/[^a-zA-Z0-9 ]/g, " ")
            .split(" ")
            .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1)))
            .join("")
        : key;
    Object.entries(gst).forEach(([k, v]) => {
      if (typeof v !== "object" || v === null) flat[nKey(k)] = v;
    });
    if (gst.gstDetails)
      Object.entries(gst.gstDetails).forEach(([k, v]) => {
        flat[nKey(k)] = v;
      });
    if (gst.address)
      Object.entries(gst.address).forEach(([k, v]) => {
        flat[nKey(k)] = v;
      });
    if (flat.gstStatus !== undefined) {
      flat.status = flat.gstStatus;
    }
    return flat;
  };

  const mapGSTToFormValuesDynamic = (gstApiResponse, gFields) => {
    const flatGST = normalizeGSTResponse(gstApiResponse);
    if (!flatGST) return {};
    const line1 = [flatGST.buildingNo, flatGST.buildingName, flatGST.flatNo].filter(Boolean).join(", ");

    const line2 = [flatGST.street, flatGST.locality, flatGST.district].filter(Boolean).join(", ");

    const city = flatGST.location || "";

    const formValues = {};
    const isInvoiceGroup = (name) => name?.toLowerCase().includes("invoic");
    const isDeliveryGroup = (name) => name?.toLowerCase().includes("deliver");
    gFields.forEach((group) => {
      const groupId = group.groupId ? String(group.groupId).toLowerCase() : null;
      const groupName = group.groupName || "";
      const isAddressGroup = isInvoiceGroup(groupName) || isDeliveryGroup(groupName);
      group.fields.forEach((field) => {
        if (!field.visible) return;
        const fieldKey = toCamelCase(field.name);
        let value = null;
        if (fieldKey === "companyName") value = flatGST.tradeName;
        if (fieldKey === "gstNo") value = flatGST.gstNo;
        if (isAddressGroup) {
          if (fieldKey === "state") value = flatGST.state;
          if (fieldKey === "pincode") value = flatGST.pincode;
          if (fieldKey === "fullAddress") value = flatGST.fullAddress;
          if (fieldKey === "line1") value = line1;
          if (fieldKey === "line2") value = line2;
          if (fieldKey === "city") value = city;
          if (fieldKey === "companyType") {
            value = normalizeStaticSelectValue(flatGST.constitutionOfBusiness, field.defaultValue);
          }
          if (fieldKey === "country") value = flatGST.country || "India";
        }
        if (value === null && flatGST[fieldKey] !== undefined) value = flatGST[fieldKey];
        if (value !== null && value !== undefined && value !== "") {
          if (groupId) {
            if (!formValues[groupId]) formValues[groupId] = {};
            formValues[groupId][fieldKey] = value;
          } else {
            formValues[fieldKey] = value;
          }
        }
      });
    });
    return formValues;
  };

  const mapApiResponseToFormFields = (apiData, gFields) => {
    const mappedValues = {};
    if (!apiData) return mappedValues;
    const lookup = {};
    const addToLookup = (obj) => {
      Object.keys(obj || {}).forEach((key) => {
        const v = obj[key];
        if (v === null || v === undefined) return;
        lookup[normalizeKey(key)] = v;
      });
    };
    addToLookup(apiData);
    Object.keys(apiData).forEach((key) => {
      if (/^group_\d+$/.test(key) && typeof apiData[key] === "object") addToLookup(apiData[key]);
    });
    gFields.forEach((group) => {
      group.fields.forEach((field) => {
        const normalizedField = normalizeKey(field.name);
        let value = lookup[normalizedField];
        if (value === undefined) return;
        const fieldKey = toCamelCase(field.name);
        if (field.type === "select" && field.dataSource?.type === "static" && field.defaultValue) {
          value = normalizeStaticSelectValue(value, field.defaultValue);
        }
        if (group.groupId) {
          mappedValues[group.groupId] = mappedValues[group.groupId] || {};
          mappedValues[group.groupId][fieldKey] = value;
        } else {
          mappedValues[fieldKey] = value;
        }
      });
    });
    return mappedValues;
  };

  // ─── PAN validation ────────────────────────────────────────────────────────

  const handleApiEnterValidation = async (e, field) => {
    if (e.key !== "Enter") return;
    if (loading) return;
    if (field?.dataSource?.type !== "api") return;
    const apiConfig = retailCustomerApiDataSources.find((api) => api.id === field.dataSource.apiSourceId);
    if (!apiConfig || apiConfig.endpoint !== "validatePANNumber") return;
    const pan = e.target.value?.trim();
    if (!pan) return;
    setLoading(true);
    try {
      const searchResponse = await getCustomer(
        form,
        parseInt(
          JSON.parse(tillData?.tillAccess?.userAccessController || "{}")?.layout === null || undefined ? 1 : JSON.parse(tillData?.tillAccess?.userAccessController || "{}")?.layout
        ),
        pan,
        setKioskUI,
        setCustomerSearchResults,
        kioskLogin,
        tillData,
        pendingRefundCustomer,
        setPendingRefundCustomer,
        isRefundCustomerEdit,
        setIsRefundCustomerEdit
      );
      const existingCustomers = searchResponse?.data?.data?.customerSearch || [];
      const isExistingCustomer = existingCustomers.length > 0;
      const panValidation = await validatePANNo(pan);
      const disabledPanStatusMap = {
        N: "INVALID PAN",
        I: "PAN INOPERATIVE",
        X: "PAN DEACTIVATED",
        F: "FAKE PAN",
        D: "PAN DELETED",
        EA: "PAN AMALGAMATION",
        EC: "PAN ACQUISITION",
        ED: "DEATH OF PAN HOLDER",
        EI: "PAN DISSOLVED",
        EL: "PAN LIQUIDATED",
        EM: "PAN MERGER",
        EP: "PAN PARTITION",
        ES: "PAN SPLIT",
        EU: "PAN UNDER LIQUIDATION",
      };
      let realStatus = "INVALID PAN";
      let isPanActuallyValid = false;
      if (disabledPanStatusMap[panValidation?.panStatusCode]) {
        realStatus = disabledPanStatusMap[panValidation.panStatusCode];
        isPanActuallyValid = false;
      } else if (panValidation?.panStatusCode === "E") {
        if (
          panValidation?.aadhaarSeedingStatusCode === "Y" ||
          panValidation?.isIndividual === false ||
          panValidation?.isIndividualAPI === false ||
          panValidation?.aadhaarSeedingStatusCode === "NA" ||
          panValidation?.aadhaarSeedingStatusCode == null
        ) {
          realStatus = "VALID & OPERATIVE";
          isPanActuallyValid = true;
        } else {
          realStatus = "VALID & INOPERATIVE";
          isPanActuallyValid = true;
        }
      }
      setPanCustomerInfo({
        panNo: panValidation.panNo,
        panStatus: panValidation.panStatus,
        aadhaarSeedingStatusCode: panValidation.aadhaarSeedingStatusCode,
        isValid: isPanActuallyValid,
        realStatus,
      });
      if (!isPanActuallyValid) {
        customerForm.setFields([{ name: field.groupId ? [field.groupId, field.name] : field.name, errors: ["Invalid PAN number"] }]);
        setIsPanUpdateMode(false);
        return;
      }
      message.success("PAN validated successfully");
      if (setIsPanCustomer) setIsPanCustomer(true);
      if (isExistingCustomer) {
        setIsPanUpdateMode(true);
        setSelectedEditOldCustomer(existingCustomers[0]);
        const panCardUrl = existingCustomers[0]?.metaData?.find((m) => m.key === "PAN_CARD_URL")?.value;
        if (panCardUrl && panCardUrl.trim() && setDocuments) {
          setDocuments((prev) => {
            const hasPan = prev.some((d) => d.docName === "PAN Card");
            if (hasPan) return prev;
            return [...prev, { uid: "pan-card", docName: "PAN Card", name: "PAN Card", status: "done", url: panCardUrl.trim(), docUrl: panCardUrl.trim() }];
          });
        }
      } else {
        setIsPanUpdateMode(false);
      }
      let sourceData = isExistingCustomer ? existingCustomers[0] : panValidation;
      const mappedFormValues = await mapApiResponseToFormFields(sourceData, groupedFields);
      const autoFillPayload = {
        ...sourceData,
        mobileno: sourceData?.mobileNo,
        lastname: sourceData?.lastName,
        lastvisitdate: sourceData?.lastVisitDate,
        cardnumber: sourceData?.cardNumber,
        city: sourceData?.customerAddress?.city,
        taxID: sourceData?.taxId,
        anniversaryDate: sourceData?.anniversaryDate?.split(" ")[0],
        birthday: sourceData?.birthday?.split(" ")[0],
        validity: sourceData?.validity?.split(" ")[0],
      };
      const touchedKeys = Object.keys(autoFillPayload);
      const preSnapshot = {};
      touchedKeys.forEach((k) => {
        preSnapshot[k] = customerForm.getFieldValue(k);
      });
      panAutoFillRef.current = { keys: touchedKeys, snapshot: preSnapshot };
      customerForm.setFieldsValue(autoFillPayload);
      const isPanMobileNo = sourceData?.mobileNo;
      setPanCustomerInfo((prev) => ({ ...prev, panHasMobile: Boolean(isPanMobileNo) }));
      setFormDataCache((prev) => ({ ...prev, [activeTab]: { ...(prev[activeTab] || {}), ...mappedFormValues } }));
    } catch (err) {
      console.error("PAN validation flow failed:", err);
      setIsPanUpdateMode(false);
    } finally {
      setLoading(false);
    }
  };

  const validatePanDirect = async (pan) => {
    if (!pan || pan.trim().length < 10) return;
    const panField = retailCustomerFields?.find(
      (f) => f.dataSource?.type === "api" && retailCustomerApiDataSources.find((a) => a.id === f.dataSource.apiSourceId && a.endpoint === "validatePANNumber")
    );
    if (!panField) return;
    await handleApiEnterValidation({ key: "Enter", target: { value: pan } }, panField);
  };

  // ─── fetchOptions ──────────────────────────────────────────────────────────

  const fetchOptions = async (field) => {
    if (field.dataSource?.type === "api") {
      const apiConfig = retailCustomerApiDataSources.find((api) => api.id === field.dataSource.apiSourceId);
      if (apiConfig?.name === "getStates") {
        const states = useReferenceStore.getState().statesData || [];
        setOptionsMap((prev) => ({ ...prev, [field.id]: states }));
      } else if (apiConfig?.name === "getCountries") {
        const countries = useReferenceStore.getState().countriesData || [];
        setOptionsMap((prev) => ({ ...prev, [field.id]: countries }));
      } else if (apiConfig?.name === "getLoyaltyData") {
        const loyaltyData = JSON.parse(localStorage.getItem("loyaltyData")) || {};
        if (loyaltyData?.loyaltyLevels) {
          const mappedOptions = loyaltyData?.loyaltyLevels?.map((item) => ({ RecordID: item.cwr_loyalty_level_id, Name: item.name })) || [];
          setOptionsMap((prev) => ({ ...prev, [field.id]: mappedOptions }));
        } else {
          try {
            const params = { startRow: 0, endRow: 10 };
            const d = await getLoyaltyInfo({ endpoint: apiConfig.endpoint, params });
            const mappedOptions = d?.loyaltyLevels?.map((item) => ({ RecordID: item.cwr_loyalty_level_id, Name: item.name })) || [];
            setOptionsMap((prev) => ({ ...prev, [field.id]: mappedOptions }));
          } catch (err) {
            console.error("Failed to load loyalty data", err);
          }
        }
      }
    } else if (field.dataSource?.type === "static") {
      const staticOptions = field.dataSource.staticOptions || [];
      setOptionsMap((prev) => ({ ...prev, [field.id]: staticOptions }));
    }
  };

  // ─── File upload ───────────────────────────────────────────────────────────

  const compressImage = (file, maxWidth = 1920, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        image.src = e.target.result;
      };
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        let width = image.width;
        let height = image.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(image, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Image compression failed"));
              return;
            }
            resolve(new File([blob], file.name, { type: file.type, lastModified: Date.now() }));
          },
          file.type,
          quality
        );
      };
      image.onerror = reject;
      reader.onerror = reject;
    });
  };

  const handleFileUpload = async (e, docName) => {
    const file = e.target.files?.[0];
    if (!file) return false;
    try {
      const fileToUpload = file.size > 4 * 1024 * 1024 && file.type.startsWith("image/") ? await compressImage(file, 1920, 0.75) : file;
      const buffer = await fileToUpload.arrayBuffer();
      const fileData = Array.from(new Uint8Array(buffer));
      const { access_token } = getOAuthHeaders() || {};
      const response = await fetch(env.uploadDocumentUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: access_token },
        body: JSON.stringify({ fileName: fileToUpload.name, fileType: fileToUpload.type, fileData }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result?.error === "string" ? result.error : JSON.stringify(result?.error) || "Upload failed");
      const { url } = result;
      if (!url) throw new Error("Upload succeeded but no URL returned");
      setDocuments((prev) => [...prev, { docID: "", docName, docUrl: url, fileName: file.name }]);
      message.success("Attachment has been uploaded succesfully.");
      return true;
    } catch (error) {
      console.error("[FileUpload] Upload error:", error);
      message.error("Failed to upload document");
      return false;
    }
  };

  // ─── Options map initialisation effect ────────────────────────────────────

  useEffect(() => {
    const countries = useReferenceStore.getState().countriesData || [];
    const states = useReferenceStore.getState().statesData || [];
    const loyaltyData = JSON.parse(localStorage.getItem("loyaltyData")) || {};
    const defaultCountryId = countries.find((c) => c.Name === "India")?.RecordID;
    const defaultStateId = tillData?.tillAccess?.csBunit?.csRegionId;
    const loyaltyLevels = loyaltyData?.loyaltyLevels || [];
    const mappedLoyaltyOptions = loyaltyLevels.map((item) => ({ RecordID: item.cwr_loyalty_level_id, Name: item.name })) || [];
    const defaultLoyaltyId = loyaltyLevels.length > 0 ? loyaltyLevels[0]?.cwr_loyalty_level_id : undefined;
    const currentValues = customerForm.getFieldsValue(true);
    const updatedValues = { ...currentValues };
    const countryFields = retailCustomerFields.filter((field) => field?.name?.toLowerCase() === "country");
    const stateFields = retailCustomerFields.filter((field) => field?.name?.toLowerCase() === "state");
    const loyaltyFields = retailCustomerFields.filter((field) => field?.name?.toLowerCase() === "loyalty level");

    countryFields.forEach((field) => {
      const fieldKey = toCamelCase(field.name);
      const gId = field.groupId ? String(field.groupId).toLowerCase() : null;
      if (gId) {
        updatedValues[gId] = updatedValues[gId] || {};
        if (!updatedValues[gId][fieldKey]) updatedValues[gId][fieldKey] = defaultCountryId;
      } else {
        if (!updatedValues[fieldKey]) updatedValues[fieldKey] = defaultCountryId;
      }
    });
    stateFields.forEach((field) => {
      const fieldKey = toCamelCase(field.name);
      const gId = field.groupId ? String(field.groupId).toLowerCase() : null;
      if (!defaultStateId) return;
      if (gId) {
        updatedValues[gId] = updatedValues[gId] || {};
        if (!updatedValues[gId][fieldKey]) updatedValues[gId][fieldKey] = defaultStateId;
      } else {
        if (!updatedValues[fieldKey]) updatedValues[fieldKey] = defaultStateId;
      }
    });
    loyaltyFields.forEach((field) => {
      const fieldKey = toCamelCase(field.name);
      const gId = field.groupId ? String(field.groupId).toLowerCase() : null;
      if (!defaultLoyaltyId) return;
      if (gId) {
        updatedValues[gId] = updatedValues[gId] || {};
        if (!updatedValues[gId][fieldKey]) updatedValues[gId][fieldKey] = defaultLoyaltyId;
      } else {
        if (!updatedValues[fieldKey]) updatedValues[fieldKey] = defaultLoyaltyId;
      }
    });
    customerForm.setFieldsValue(updatedValues);
    setOptionsMap((prev) => {
      const newMap = { ...prev };
      countryFields.forEach((f) => {
        newMap[f.id] = countries;
      });
      stateFields.forEach((f) => {
        newMap[f.id] = states;
      });
      loyaltyFields.forEach((f) => {
        newMap[f.id] = mappedLoyaltyOptions;
      });
      return newMap;
    });
  }, [displayAddNewCustomer, displayEditOldCustomer]);

  // ─── B2B no-results auto-fill effect ──────────────────────────────────────

  useEffect(() => {
    if (!isB2BEnabled) return;
    if (!noB2BResults) return;
    const updatedValues = {};
    groupedFields.forEach((group) => {
      group.fields.forEach((field) => {
        const fieldName = field.name.toLowerCase();
        if (searchByName && fieldName === "company name") {
          if (group.groupId) {
            updatedValues[group.groupId] = { ...(updatedValues[group.groupId] || {}), [field.name]: "" };
          } else {
            updatedValues[field.name] = "";
          }
        }
        if (!searchByName && fieldName.includes("gst")) {
          if (group.groupId) {
            updatedValues[group.groupId] = { ...(updatedValues[group.groupId] || {}), [field.name]: "" };
          } else {
            updatedValues[field.name] = "";
          }
        }
      });
    });
    customerForm.setFieldsValue(updatedValues);
    setFormDataCache((prev) => ({ ...prev, [activeTab]: { ...(prev[activeTab] || {}), ...updatedValues } }));
  }, [noB2BResults, searchByName]);

  useEffect(() => {
    if (b2bSearchResults.length > 0) setNoB2BResults(false);
  }, [b2bSearchResults]);

  return {
    // refs
    panAutoFillRef,
    // state
    optionsMap,
    b2bSearchResults,
    setB2bSearchResults,
    b2bLoading,
    searchPending,
    searchByName,
    setSearchByName,
    noB2BResults,
    // handlers / mappers
    handleChange,
    searchB2BCustomers,
    mapB2BDataDynamic,
    fillAddressFields,
    mapGSTToFormValuesDynamic,
    mapApiResponseToFormFields,
    handleApiEnterValidation,
    validatePanDirect,
    fetchOptions,
    handleFileUpload,
    // utils
    toCamelCase,
    normalizeKey,
    normalizeStaticSelectValue,
    getStaticDisplayValue,
  };
};

export default useCustomerFormData;
